import React, { useState } from 'react';
import { TextField, Button, Box, Alert } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import PasswordRequirements from './PasswordRequirements';
import { validatePassword } from '../utils/passwordValidation';

interface PasswordResetFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export const PasswordResetForm: React.FC<PasswordResetFormProps> = ({ onSuccess, onCancel }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const { updatePasswordWithRecovery } = useAuth();
  const { showError, showSuccess } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');
    
    const passwordValidation = validatePassword(newPassword, confirmPassword);
    if (!passwordValidation.isValid) {
      const unmetRequirements = passwordValidation.requirements
        .filter(req => !req.met)
        .map(req => req.label);
      setValidationError(`Password must meet the following requirements: ${unmetRequirements.join(', ')}`);
      return;
    }

    setLoading(true);
    try {
      await updatePasswordWithRecovery(newPassword);
      showSuccess('Password updated successfully! You are now logged in.');
      onSuccess();
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      {validationError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationError}
        </Alert>
      )}
      
      <TextField
        fullWidth
        type="password"
        label="New Password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        margin="normal"
        required
        disabled={loading}
        sx={{ mb: 2 }}
      />
      
      {/* Show password requirements */}
      <PasswordRequirements password={newPassword} confirmPassword={confirmPassword} />

      <TextField
        fullWidth
        type="password"
        label="Confirm New Password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        margin="normal"
        required
        disabled={loading}
        error={newPassword.length > 0 && (confirmPassword.length === 0 || newPassword !== confirmPassword)}
        helperText={
          newPassword.length > 0 && confirmPassword.length === 0 
            ? "Please confirm your password"
            : confirmPassword.length > 0 && newPassword !== confirmPassword 
              ? "Passwords do not match" 
              : ""
        }
        inputProps={{ minLength: 8 }}
        sx={{ mb: 3 }}
      />

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          Cancel
        </Button>
        
        <Button
          type="submit"
          variant="contained"
          disabled={loading || !newPassword || !confirmPassword}
          sx={{ minWidth: 120 }}
        >
          {loading ? 'Updating...' : 'Update Password'}
        </Button>
      </Box>
    </Box>
  );
};
